import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

// --- Icon components ---
const SpinnerIcon = ({ className = "animate-spin h-12 w-12 text-indigo-500 mx-auto mb-4" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const ErrorTriangleIcon = ({ className = "w-12 h-12 text-red-500 mx-auto mb-4" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.174 3.35 1.94 3.35h14.72c1.766 0 2.806-1.85 1.94-3.35L12 2.25 2.697 15.626zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

const PlusCircleIcon = ({ className = "h-5 w-5 mr-3" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z" clipRule="evenodd" />
  </svg>
);

const DocumentTextIcon = ({ className = "mx-auto h-16 w-16 text-gray-400 mb-4" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// --- constants ---
const NA_TEXT_STYLE = "text-gray-400 italic";
const DEFAULT_PAGE_SIZE = 10;

// --- CSV export utility ---
const exportToCsv = (filename, rows) => {
  if (!rows || !rows.length) return;
  const keys = Object.keys(rows[0]);
  const lines = [keys.join(',')];
  for (const row of rows) {
    const vals = keys.map(k => {
      let v = row[k] ?? '';
      v = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(v) ? `"${v}"` : v;
    });
    lines.push(vals.join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// --- Count badge (circular pill like screenshot) ---
const CountBadge = ({ value = 0, color = 'gray' }) => {
  // inline styles to avoid external CSS
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: 999,
    fontWeight: 700,
    fontSize: 12,
    minWidth: 30,
  };
  const colors = {
    gray: { background: '#f5f7fa', color: '#2d3748' },
    blue: { background: '#e8f1ff', color: '#1e3a8a' },
    green: { background: '#eafaf0', color: '#047857' },
  };
  const style = { ...base, ...(colors[color] || colors.gray) };
  return <span style={style}>{value}</span>;
};

// --- date format helper ---
const fmtDate = (iso) => {
  if (!iso) return <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>N/A</span>;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// --- Vendor row (with country fallback and RFQ badges) ---
// --- Vendor Type Badge ---
const VendorTypeBadge = ({ types }) => {
  if (!Array.isArray(types) || types.length === 0) {
    return <span className={NA_TEXT_STYLE}>N/A</span>;
  }
  const colorMap = {
    'Fieldwork': { bg: '#dbeafe', text: '#1e40af' },
    'Panel': { bg: '#fef3c7', text: '#92400e' },
    'Recruitment': { bg: '#d1fae5', text: '#065f46' },
    'Translation': { bg: '#ede9fe', text: '#5b21b6' },
  };
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t, i) => {
        const colors = colorMap[t] || { bg: '#f3f4f6', text: '#374151' };
        return (
          <span
            key={i}
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 500,
            }}
          >
            {t}
          </span>
        );
      })}
    </div>
  );
};

const VendorTableRow = React.memo(({ vendor, onDelete, isLoading }) => {
  const countryDisplay = (() => {
    if (Array.isArray(vendor.countries) && vendor.countries.length) return vendor.countries.join(', ');
    if (Array.isArray(vendor.uae_cities) && vendor.uae_cities.length) return vendor.uae_cities.join(', ') + ' (UAE city)';
    if (Array.isArray(vendor.uae_offices) && vendor.uae_offices.length) return vendor.uae_offices.join(', ') + ' (UAE office)';
    return null;
  })();

  const awarded = Number(vendor.awarded_rfqs_count ?? 0);
  const participated = Number(vendor.participated_rfqs_count ?? 0);

  return (
    <tr className="hover:bg-slate-50 transition-colors duration-150">
      <td className="px-5 py-4 whitespace-nowrap align-top">
        <Link to={`/vendors/${vendor.id}`} className="text-base font-semibold text-indigo-600 hover:text-indigo-800 hover:underline">
          {vendor.name || <span className={NA_TEXT_STYLE}>Unnamed Vendor</span>}
        </Link>
        {vendor.contacts && vendor.contacts.length > 0 && (
          <div className="text-xs text-gray-500 mt-1">
            {vendor.contacts[0].name}{vendor.contacts[0].email ? ` · ${vendor.contacts[0].email}` : ''}
          </div>
        )}
      </td>

      <td className="px-5 py-4 whitespace-nowrap text-sm align-top">
        <VendorTypeBadge types={vendor.vendor_type} />
      </td>

      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700 align-top">
        {countryDisplay ?? <span className={NA_TEXT_STYLE}>N/A</span>}
      </td>

      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700 align-top text-center">
        <CountBadge value={awarded} color={awarded > 0 ? 'green' : 'gray'} />
      </td>

      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-700 align-top text-center">
        <CountBadge value={participated} color={participated > 0 ? 'blue' : 'gray'} />
      </td>

      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
        {fmtDate(vendor.created_at)}
      </td>

      <td className="px-5 py-4 whitespace-nowrap text-sm font-medium align-top">
        <div className="flex items-center gap-2">
          <Link to={`/vendors/${vendor.id}`} className="text-indigo-600 hover:text-indigo-800 hover:underline">View</Link>
          <span className="text-gray-300">|</span>
          <Link to={`/vendors/${vendor.id}/edit`} className="text-indigo-600 hover:text-indigo-800 hover:underline">Edit</Link>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => onDelete(vendor.id)}
            className="text-red-600 hover:text-red-800 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
            aria-label={`Delete ${vendor.name}`}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
});

// --- Vendor table ---
const VendorTable = ({ vendors, onDelete, isLoading }) => (
  <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
          <tr>
            <th className="px-5 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Vendor</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Countries</th>
            <th className="px-5 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Awarded</th>
            <th className="px-5 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Submitted</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Created</th>
            <th className="px-5 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>

        <tbody className="bg-white divide-y divide-gray-100">
          {vendors.map(v => (
            <VendorTableRow key={v.id} vendor={v} onDelete={onDelete} isLoading={isLoading} />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// --- Header & empty state ---
const VendorListPageHeader = ({ onAddNewVendor, total, onExportCsv }) => (
  <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
    <div>
      <h1 className="text-3xl font-extrabold text-slate-900 leading-tight">Vendor Directory</h1>
      <p className="mt-1 text-sm text-slate-600">Manage vendors — {total} record{total !== 1 ? 's' : ''} available.</p>
    </div>

    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onExportCsv}
        className="inline-flex items-center px-4 py-2 rounded-md border border-black bg-black text-sm text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black/40"
      >
        Export CSV
      </button>

      <button
        onClick={onAddNewVendor}
        className="inline-flex items-center px-5 py-2.5 rounded-md shadow-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transform hover:scale-[1.02]"
      >
        <PlusCircleIcon />
        Add New Vendor
      </button>
    </div>
  </div>
);

const EmptyStateDisplay = ({ onActionClick }) => (
  <div className="py-20 px-6 bg-white shadow rounded-lg border border-gray-200 text-center">
    <DocumentTextIcon />
    <h3 className="mt-3 text-xl font-semibold text-slate-900">No vendors yet</h3>
    <p className="mt-2 text-sm text-slate-600">Create your first vendor to get started. You can also import vendors using CSV.</p>
    <div className="mt-6">
      <button
        onClick={onActionClick}
        className="inline-flex items-center px-5 py-2.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        Add New Vendor
      </button>
    </div>
  </div>
);

// === Main VendorList component ===
const VendorList = () => {
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // search / filter / pagination
  const [q, setQ] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE); // 10 per page

  const navigate = useNavigate();
  const mounted = useRef(true);

  const fetchVendors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch with large page size to get all vendors in one request
      // Backend max is 200, which should be sufficient for most use cases
      const res = await api.get('vendors/?page_size=200');
      const data = res.data;
      let list = [];

      if (Array.isArray(data)) {
        list = data;
      } else if (data && Array.isArray(data.results)) {
        list = data.results;
        // If there are more pages, fetch them all
        if (data.next) {
          let nextUrl = data.next;
          while (nextUrl) {
            const nextRes = await api.get(nextUrl);
            if (nextRes.data && Array.isArray(nextRes.data.results)) {
              list = [...list, ...nextRes.data.results];
            }
            nextUrl = nextRes.data?.next || null;
          }
        }
      } else if (data && Array.isArray(data.items)) {
        list = data.items;
      } else {
        console.warn('Unexpected vendor payload:', data);
        throw new Error('Unexpected data format from server');
      }

      if (!mounted.current) return;
      setVendors(list);
    } catch (err) {
      console.error('Error fetching vendors', err);
      let msg = 'Failed to load vendors. Please try again.';
      if (err.response?.data?.detail) msg = err.response.data.detail;
      else if (err.message) msg = err.message;
      setVendors([]);
      setError(msg);
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchVendors();
    return () => { mounted.current = false; };
  }, [fetchVendors]);

  const handleDelete = useCallback(async (vendorId) => {
    const ok = window.confirm('Delete vendor? This cannot be undone.');
    if (!ok) return;
    setIsLoading(true);
    try {
      await api.delete(`vendors/${vendorId}/`);
      await fetchVendors();
      alert('Vendor deleted successfully.');
    } catch (err) {
      console.error('Delete error', err);
      alert(err.response?.data?.detail || err.message || 'Failed to delete vendor');
      setIsLoading(false);
    }
  }, [fetchVendors]);

  const handleAddNewVendor = useCallback(() => navigate('/vendors/new'), [navigate]);

  // unique countries list for filter (derived)
  const countries = useMemo(() => {
    const setC = new Set();
    vendors.forEach(v => {
      if (Array.isArray(v.countries)) v.countries.forEach(c => c && setC.add(c));
    });
    return Array.from(setC).sort();
  }, [vendors]);

  // filtered + sorted list
  const filtered = useMemo(() => {
    const qs = q.trim().toLowerCase();
    let arr = vendors.filter(v => {
      if (qs) {
        const inName = (v.name || '').toLowerCase().includes(qs);
        const inUploader = (v.created_by_username || '').toLowerCase().includes(qs);
        const inExpertise = (v.expertise || '').toLowerCase().includes(qs);
        return inName || inUploader || inExpertise;
      }
      return true;
    });
    if (countryFilter) {
      arr = arr.filter(v => Array.isArray(v.countries) && v.countries.includes(countryFilter));
    }
    arr.sort((a, b) => {
      const ta = new Date(a.created_at || a.uploaded_at || 0).getTime();
      const tb = new Date(b.created_at || b.uploaded_at || 0).getTime();
      return tb - ta;
    });
    return arr;
  }, [vendors, q, countryFilter]);

  // pagination
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const handleExportCsv = useCallback(() => {
    const rows = filtered.map(v => ({
      id: v.id,
      name: v.name,
      vendor_type: Array.isArray(v.vendor_type) ? v.vendor_type.join('; ') : '',
      countries: Array.isArray(v.countries) ? v.countries.join('; ') : '',
      expertise: v.expertise || '',
      awarded_rfqs: v.awarded_rfqs_count || 0,
      submitted_rfqs: v.participated_rfqs_count || 0,
      created_by: v.created_by_username || '',
      created_at: v.created_at || v.uploaded_at || '',
    }));
    exportToCsv(`vendors_${new Date().toISOString().slice(0,10)}.csv`, rows);
  }, [filtered]);

  // loading / error UIs
  if (isLoading && vendors.length === 0 && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-slate-50">
        <div className="bg-white shadow rounded-xl p-8 max-w-md w-full text-center">
          <SpinnerIcon />
          <p className="text-xl font-semibold text-slate-900">Loading vendors...</p>
          <p className="mt-2 text-sm text-slate-500">Hold tight — fetching the latest vendor data.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-slate-50">
        <div className="bg-white shadow rounded-xl p-8 max-w-md w-full text-center border border-red-100">
          <ErrorTriangleIcon />
          <p className="text-xl font-semibold text-red-700">Error loading vendors</p>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <div className="mt-6">
            <button
              onClick={fetchVendors}
              className="inline-flex items-center px-5 py-2.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // main layout
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <VendorListPageHeader onAddNewVendor={handleAddNewVendor} total={total} onExportCsv={handleExportCsv} />

        {/* Controls */}
        <div className="mb-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex items-center gap-2 w-full md:w-1/2">
            <label htmlFor="vendor-search" className="sr-only">Search vendors</label>
            <input
              id="vendor-search"
              type="search"
              placeholder="Search by name, expertise, or created by..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              className="w-full md:w-80 px-3 py-2 border border-slate-200 rounded-md shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              aria-label="Search vendors"
            />
            {q && (
              <button
                onClick={() => { setQ(''); setPage(1); }}
                className="px-3 py-2 text-sm text-slate-600 bg-slate-100 rounded-md hover:bg-slate-200"
                aria-label="Clear search"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <select
              value={countryFilter}
              onChange={(e) => { setCountryFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-md bg-white"
              aria-label="Filter by country"
            >
              <option value="">All countries</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <div className="text-sm text-slate-600">
              Showing <strong>{(paged && paged.length) || 0}</strong> of <strong>{total}</strong>
            </div>
          </div>
        </div>

        {/* Content */}
        {paged.length === 0 ? (
          <EmptyStateDisplay onActionClick={handleAddNewVendor} />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="block md:hidden space-y-3 mb-4">
              {paged.map(v => (
                <div key={v.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <Link to={`/vendors/${v.id}`} className="text-lg font-semibold text-indigo-600 hover:underline block truncate">
                        {v.name || <span className={NA_TEXT_STYLE}>Unnamed Vendor</span>}
                      </Link>
                      <div className="mt-2">
                        <VendorTypeBadge types={v.vendor_type} />
                      </div>
                      <div className="text-sm text-slate-500 mt-2">
                        <strong>Countries:</strong> {Array.isArray(v.countries) && v.countries.length ? v.countries.join(', ') : 'N/A'}
                      </div>
                      <div className="mt-3 flex gap-3 items-center flex-wrap">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">Awarded:</span>
                          <CountBadge value={Number(v.awarded_rfqs_count ?? 0)} color={Number(v.awarded_rfqs_count ?? 0) > 0 ? 'green' : 'gray'} />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500">Submitted:</span>
                          <CountBadge value={Number(v.participated_rfqs_count ?? 0)} color={Number(v.participated_rfqs_count ?? 0) > 0 ? 'blue' : 'gray'} />
                        </div>
                        <div className="text-xs text-slate-400">{fmtDate(v.created_at)}</div>
                      </div>
                    </div>
                    <div className="text-right flex flex-col gap-2 shrink-0">
                      <Link to={`/vendors/${v.id}`} className="text-indigo-600 text-sm hover:underline">View</Link>
                      <Link to={`/vendors/${v.id}/edit`} className="text-indigo-600 text-sm hover:underline">Edit</Link>
                      <button
                        onClick={() => handleDelete(v.id)}
                        className="text-red-600 text-sm hover:underline"
                        disabled={isLoading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
              <VendorTable vendors={paged} onDelete={handleDelete} isLoading={isLoading} />
            </div>

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-slate-600">Page {page} of {totalPages}</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border bg-white hover:bg-slate-50 disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border bg-white hover:bg-slate-50 disabled:opacity-50"
                >
                  1
                </button>
                {totalPages > 3 && page > 2 && <span className="px-2">…</span>}
                {totalPages > 1 && (
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 rounded border bg-white hover:bg-slate-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VendorList;
